import { IImageTypes, ISelectOption } from 'components';
import { TimePickerValue } from 'react-time-picker';
import { Contact, Hour, IBoostGroupModel, ImageTypes, SMSReceiptInterface } from './boostGroup';
import { CpoConnectorStatus } from './cpo';
import { AddressInterface } from './owner';
import { ISiteAddress, ISiteConfiguration } from './site';

export interface BoostDeviceInterfaceSplunk {
  reportInfo?: any; // Any from the server.
  chargeEnergy?: any;
  sessionsCount?: any;
  sessionHoursCount?: any;
  boostStatus?: any;
  leftConnector?: any;
  rightConnector?: any;
}

export interface IReports {
  rows: any[];
  fields: string[];
  boostGroupId?: number;
}

export interface BoostGroupsInterface {
  id: number;
  name: string;
  userId: number;
  ownerId: number;
  cpoCountryCode?: string;
  cpoPartyId?: number;
  languageId: number;
  level: number;
  isSite: boolean;
  parentId?: null;
  createdAt?: Date;
  updatedAt?: Date;
  mainColorTheme?: string;
  secondaryColorTheme?: string;
  Boost?: BoostDevices;
  boostsCount?: string;
}

export interface BoostDeviceInterface extends BoostDeviceInterfaceSplunk {
  groupId: number;
  serialNumber: string;
  ownerId?: number;
  boostGroupId?: number;
  BoostGroup: BoostGroupsInterface;
  subGroupId?: number;
  subBoostGroupId?: number;
  totalBoostGroups?: number;
  totalBoost?: number;
  geoLocation?: IGeoLocation;
  address?: AddressInterface;
  addressId?: number;
  id?: number;
  boostGroupName: string;
  boostName?: string;
  name?: string;
  createdAt?: string;
  networkId?: string;
  networkURL?: string;
  updatedAt?: string;
  clientId?: any;
  connectors?: BoostDeviceConnector[];
}

export interface BoostDeviceConnectorInterface {
  id?: number;
  connectorSide: string;
  connectorType: string;
  status: string;
  maxAmperage: number;
  maxVoltage: number;
  maxElectricPower: number;
  format: string;
  manuallyDisabled: boolean;
  powerType: string;
  evseUid?: any;
}

export interface BoostDeviceRowInterface {
  id?: number;
  name?: string;
  serialNumber?: string;
  clientId?: string;
  model?: any;
  connectorDetails?: BoostDeviceConnectorInterface[];
  componentDetails?: any;
  ownerId?: number;
  groupId?: number;
  lastResetDate?: any;
  address?: AddressInterface;
}

export interface BoostDevices {
  boostGroupRelationsId: number;
  boostId: number;
  boostGroupId: number;
  createdAt: Date;
  updatedAt: Date;
  Boost: BoostInterface;
  BoostGroup: BoostGroupsInterface;
  unassigned?: boolean;
  boostName?: string;
  boostSerialNumber?: string;
}

export interface BoostDevicesResponse {
  count: number;
  rows?: BoostDevices[];
}

export interface BoostInterface {
  boostId: number;
  boostName: string;
  boostSerialNumber: string;
  networkURL: string;
  technicalStatus: string;
  createdAt?: Date;
  updatedAt?: Date;
  unassigned?: boolean;
}

export interface IGeoLocation {
  location_type?: string;
  formatted_address?: string;
  lat?: number;
  lng?: number;
}

export interface IGeometry {
  address_components: Array<any>;
  formatted_address: string;
  geometry: {
    location: { lat: number; lng: number };
    location_type: string;
  };
  place_id: string;
  types: Array<string>;
}

export interface IConnectorCount {
  countAvailable: number;
  countCharging: number;
  countDisabled: number;
  countUnavailable: number;
  countUnknown: number;
  countClosed: number;
}

export const ConnectorStatus = {
  ...CpoConnectorStatus,
  available: 'AVAILABLE',
  authorizing: 'AUTHORIZING',
  aborting: 'ABORTING',
  charging: 'CHARGING',
  closed: 'CLOSED',
  diverted: 'DIVERTED',
  finishing: 'FINISHING',
  initializing: 'INITIALIZING',
  negotiating: 'NEGOTIATING',
  offline: 'OFFLINE',
  disabled: 'DISABLED',
  preparing: 'PREPARING',
  safety_test: 'SAFETY_TEST',
  unavailable: 'UNAVAILABLE',
  inoperative: 'INOPERATIVE',
  wait_for_cable: 'WAIT_FOR_CABLE',
  wait_for_start: 'WAIT_FOR_START',
  outoforder: 'OUTOFORDER'
};

export interface UtilizationSummaryIcon {
  name: string;
  color?: string;
  type?: string;
}

export interface UtilizationSummaryItem {
  name: string;
  value: string | number;
  order: number;
  textIcon?: UtilizationSummaryIcon;
  tooltip?: string;
  unit?: string;
}

export const DEFAULT_UTILIZATION_SUMMARY: UtilizationSummaryItem[] = [
  {
    name: 'Total_Charging_Sessions',
    value: 0,
    order: 1
  },
  {
    name: 'Avg_Charging_Duration',
    value: 0,
    order: 2
  },
  {
    name: 'Total_Charging_Duration',
    value: 0,
    order: 3
  },
  {
    name: 'CCS',
    value: 0,
    order: 4
  },
  {
    name: 'CHAdemo',
    value: 0,
    order: 5
  },
  {
    name: 'Avg_Energy_Dispensed',
    value: 0,
    order: 6
  },
  {
    name: 'Total_Energy_Dispensed',
    value: 0,
    order: 7
  }
];

export enum ReportType {
  connectorStatus = 'BOOST_CONNECTOR_STATUS',
  utilizationSummary = 'UTILIZATION_SUMMARY_REPORT',
  uptimeSummary = 'AFBReport7_Uptime',
  barChartDays = 'CHARGING_SESSION_DAY',
  barChartHours = 'CHARGING_SESSION_HOUR',
  stateOfCharge = 'STATE_OF_CHARGE',
  demandChargeSavings = 'BOOST_DEMAND_CHARGE_SAVINGS'
}

export enum BoostMapsStrings {
  splunkInfoDate = 'splunkInfoDate',
  kWh = 'kWh',
  Mins = 'Mins',
  Energy = 'Energy',
  Charging = 'Charging'
}

export interface IBoost {
  addressId?: number;
  boostGroupId?: number;
  id: number;
  clientId: string;
  name: string;
  serialNumber: string;
  networkId?: number;
  networkURL?: string;
  ownerId?: number;
  status?: string;
  subBoostGroupId?: number;
}

export interface IBoostGroup {
  id?: string;
  boostGroupId: number;
  boostGroupName: string;
  name?: string;
  level: number;
  subBoostGroupName?: string;
  subBoostGroupId?: string;
  ownerId?: number;
  Owner: {
    ownerId: number;
    ownerName: string;
  };
  userId?: number;
  lowStateOfChargeValue?: number;
  networkURL: string;
  BoostGHrsOps: IBoostGroupHours[];
  Network?: IBoostGroupNetwork | null;
  Pricings: PricingModelInterface[];
  Contact?: IContact;
  Image?: IImage;
  Logo?: IImage;
  logoId?: number;
  imageId?: number;
  helpImageId?: number;
  TimeZone: ITimesZones;
  cpoCountryCode?: string;
  cpoPartyId?: string;
  autoRechargingSOCLimit?: number;
  Currency?: {
    currencyId: number;
  };
  Address: AddressInterface;
  address: AddressInterface;
  AmenitiesPointOfInterests: ISiteAddress[];
  Amenities: any; //Because of the backed.
  PointOfInterests: ISiteAddress[];
  isSite: boolean;
  timeOfUses: IBoostGroupHours[];
  country: string;
  languageId?: number;
  mainColorTheme?: string;
  secondaryColorTheme?: string;
  publishToEVNetwork: boolean;
  Boosts: IBoost[];
}

export interface IBoostGroupNetwork {
  networkId?: number | null;
  networkName?: string;
  url?: string | null;
}

export interface IContact {
  contactId?: number;
  firstName: string;
  lastName: string;
  phone: string;
  region?: string;
}

export interface ICountries {
  countryId: number;
  code: string;
  name: string;
}

export interface ITimesZones {
  id: number;
  name: string;
  country?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ICurrencies {
  currencyId: string;
  currencyName: string;
  currencySymbol: string;
}

export interface ILanguages {
  languageId: number;
  languageName: string;
  createdAt: string;
  updatedAt: string;
}

export enum IBoostWeekDays {
  monday = 'Monday',
  tuesday = 'Tuesday',
  wednesday = 'Wednesday',
  thursday = 'Thursday',
  friday = 'Friday',
  saturday = 'Saturday',
  sunday = 'Sunday'
}

export interface IDayOfWeek {
  day: number;
  dayName: IBoostWeekDays;
}

export interface IBoostGroupHours {
  timeOfUseId?: number;
  boostGHrsOpId?: number;
  dayName?: IBoostWeekDays;
  /** Monday as 0 */
  day: number;
  active: boolean;
  openTime: TimePickerValue;
  closeTime: TimePickerValue;
  boostGroupId?: string;
  subBoostGroupId?: string;
  error?: string;
}

export interface IBoostGroupHoursData {
  boostGHrsOp?: IBoostGroupHours[];
  timeOfUSe?: IBoostGroupHours[];
  timeZone?: string;
}
export interface ITaxRateInterface {
  name: string;
  taxRate: number;
}
export interface PricingModelInterface {
  rate?: number;
  name?: string;
  pricingType?: string;
  preAuthAmount?: number;
  minimumPayment?: number;
  taxRates?: ITaxRateInterface[];
  dischargingType?: string;
  paymentTermialType?: string;
}

export interface PricingTypeInterface {
  pricingTypeId: number;
  pricingName: string;
  status?: boolean;
}

export type EncodeImage = string | ArrayBuffer | null | undefined;

export interface IImage {
  siteImagesId?: number;
  ownerId?: number;
  image?: { data: any };
  imageSize?: number;
  imageUrl?: string;
  logoUrl?: string;
  preview?: string;
  selected?: boolean;
  updatedAt?: string;
  id?: string | number;
}

export interface BoostDeviceListProps {
  boostGroupId?: number;
  isSubGroup?: boolean | undefined;
  unassigned?: boolean;
  boostGroupIdFromParam?: number;
}

export interface ImageDetailsResponseInterface {
  count: number;
  rows: IImage[];
}

export interface IHoursOfOperationResponse {
  count: number;
  rows: IBoostGroupHours[];
}

export interface DischargingType {
  id: number;
  name: string;
  label: string;
  createdAt: string;
  updatedAt: string;
}

export interface BoostAddDeviceInterfaceSplunk {
  reportInfo?: any; // Any from the server.
  chargeEnergy?: any;
  sessionsCount?: any;
  sessionHoursCount?: any;
  boostStatus?: any;
  leftConnector?: string;
  rightConnector?: string;
}

export interface SiteConfigurationProps {
  subBoostGroupId: number;
  siteConfiguration: ISiteConfiguration | undefined;
  openSnackBar: any;
  ownerId: string;
  publishToEVNetworkSwitch?: any;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  updateBoostSiteAddress: any;
}

export interface BoostGroupReceiptsProps {
  ownerId: string;
  boostGroupId: number;
  hasSubgroups: boolean;
  smsReceipt?: SMSReceiptInterface;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}
export interface BoostGroupDeviceConfigurationProps {
  boostGroupConfig?: IBoostGroupModel;
  boostDeviceParams: BoostDeviceListProps;
  boostGroupId: number;
  pricing?: PricingModelInterface[];
  roleId: number | undefined;
  subBoostGroupId: number;
  setBoostGroupConfig?: (newConfig: IBoostGroupModel) => void | undefined;
  openSnackBar: any;
  ownerId: string;
  publishToEVNetworkSwitch?: any;
  hasSubgroups: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface BoostGroupHoursProps {
  ownerId?: string;
  boostGroupId?: number;
  isTimeUseSection: boolean;
  isSubGroup?: boolean;
  autoRechargingSOCLimit?: number;
  openSnackBar: any;
  allHours?: Hour[];
  hasSubgroups: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface BoostNetwork {
  networkId: number | null | undefined;
  networkURL: string | null;
}

export interface BoostGroupNetworkProps {
  ownerId: string;
  boostGroupId: number;
  subBoostGroupId?: number;
  networkURL?: string;
  network?: string | null;
  pricing?: PricingTypeInterface[];
  networks: ISelectOption[];
  openSnackBar: any;
  hasSubgroups: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface BoostGroupPricingProps {
  activePricing?: PricingModelInterface;
  ownerId: string;
  boostGroupId: number;
  subBoostGroupId: number;
  country?: string;
  openSnackBar: any;
  currency?: string;
  hasSubgroups: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface BoostGroupImageProps {
  type:
    | ImageTypes.GROUP_BACKGROUND
    | ImageTypes.GROUP_LOGO
    | ImageTypes.GROUP_HELP_PAGE_CONTENT
    | ImageTypes.GROUP_PRICE;
  title: string;
  ownerId: string;
  boostGroupId: number;
  boostGroupImages?: IImageTypes[];
  setBoostGroupConfig?: (newConfig: IBoostGroup) => void;
  openSnackBar: any;
  hasSubgroups: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface BoostGroupSupportProps {
  contact?: Contact;
  ownerId: string;
  boostGroupConfig?: IBoostGroupModel;
  boostGroupId: number;
  setBoostGroupConfig?: (newConfig: IBoostGroupModel) => void;
  subBoostGroupId?: number;
  onUpdate?: VoidFunction;
  hasSubgroups: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface BoostDeviceConnector {
  connectorSide: string;
  type: string; //ConnectorTypes CATALOG
  status: string; // EVSEStatus CATALOG
  format: string; // ConnectorFormats CATALOG
  powerType: string; // ConnectorPowerTypes CATALOG
  maxAmperage: number;
  maxVoltage: number;
  manuallyDisabled: boolean;
  cpoConnectorId: string; // values ONE or TWO there is no catalog for this
}

export interface IBoostColor {
  value: string;
  label: string;
}

export interface BoostGroupThemeProps {
  mainColorTheme?: string;
  ownerId: string | null;
  boostGroupId: number;
  subBoostGroupId: number;
  openSnackBar: any;
  hasSubgroups: any;
  setLoading: any;
}

export interface IBoostAddDevice extends BoostAddDeviceInterfaceSplunk {
  serialNumber?: string;
  ownerId?: number;
  groupId?: number;
  totalBoostGroups?: number;
  totalBoost?: number;
  geoLocation?: IGeoLocation;
  address?: AddressInterface;
  connectorDetails?: BoostDeviceConnector[];
  addressId?: number;
  id?: number;
  evsuid?: string;
  boostGroupName?: string;
  name?: string;
  createdAt?: string;
  networkId?: string;
  networkURL?: string;
  status?: string;
  updatedAt?: string;
  clientId?: any;
  rfidEnable: boolean;
  operationSettings: any;
}

export interface ISocData {
  time: string;
  soc: number;
}

export interface IDemandChargeData {
  time: string;
  wIn: number;
  wOut: number;
}

export interface IPricingModel {
  pricingId?: string;
  pricingRate?: number;
  pricingName?: string;
  pricingTypeId?: number;
  preAuthAmount?: number;
  minimumPayment?: number;
  dischargingTypeId?: number | undefined;
  dischargingLowerLimit?: number | undefined;
  dischargingHigherLimit?: number | undefined;
  // This turns into a component icon
  status?: any;
  createdAt?: any;
  updatedAt?: any;
}

export const addressFields = (disabled = false, countries?: any) => {
  const required = true;
  return [
    {
      label: 'Address',
      name: '[address][addressLine1]',
      required,
      disabled
    },
    {
      label: 'City',
      name: '[address][city]',
      required,
      disabled
    },
    {
      label: 'State',
      name: '[address][state]',
      required,
      disabled
    },
    {
      label: 'Zip Code',
      name: '[address][zipCode]',
      required,
      disabled
    },
    {
      label: 'Country',
      name: '[address][countryCode]',
      required,
      options: countries,
      disabled
    }
  ];
};

export interface ISocData {
  time: string;
  soc: number;
}

export interface IDemandChargeData {
  time: string;
  wIn: number;
  wOut: number;
}

export interface IPricingModel {
  pricingId?: string;
  pricingRate?: number;
  pricingName?: string;
  pricingTypeId?: number;
  preAuthAmount?: number;
  minimumPayment?: number;
  dischargingTypeId?: number | undefined;
  dischargingLowerLimit?: number | undefined;
  dischargingHigherLimit?: number | undefined;
  // This turns into a component icon
  status?: any;
  createdAt?: any;
  updatedAt?: any;
}

export interface ISiteManagementAccordions {
  title: string;
  icon: any;
  content: any;
}

const CPOConnectorIdKeys = [{ key: 'ONE' }, { key: 'TWO' }];

export const CPOConnectorIds = CPOConnectorIdKeys.map((v) => ({
  key: v.key,
  value: v.key,
  label: v.key
}));

export const DEFAULT_CONNECTOR_STATUS: IConnectorCount = {
  countAvailable: 0,
  countCharging: 0,
  countDisabled: 0,
  countUnavailable: 0,
  countUnknown: 0,
  countClosed: 0
};

export const defaultBoostAddress = {
  type: 'OWNER',
  addressLine1: '7200 Gateway Blvd',
  city: 'Newark',
  state: 'California',
  zipCode: '94560',
  countryCode: 'USA',
  latitude: '',
  longitude: ''
};

const boostLedColors: ISelectOption[] = [
  {
    value: 'GREEN',
    label: 'Green'
  },
  {
    value: 'BLUE',
    label: 'Blue'
  },
  {
    value: 'ORANGE',
    label: 'Orange'
  }
];

export enum BoostLookUpTypes {
  CONNECTOR_STATUS = 'CONNECTOR_STATUS',
  CONNECTOR_TYPE = 'CONNECTOR_TYPE',
  CONNECTOR_SIDE = 'CONNECTOR_SIDE',
  FORMAT_TYPE = 'FORMAT_TYPE',
  POWER_TYPE = 'POWER_TYPE',
  ASSET_STATUS = 'ASSET_STATUS',
  DEVICE_INPUT_VOLTAGE_SETTING = 'DEVICE_INPUT_VOLTAGE_SETTING'
}

export const BoostGroupFields = (
  ocpiCountryCode?: string,
  ocpiPartyId?: string,
  countries?: ISelectOption[],
  timeZones?: ISelectOption[],
  languages?: ISelectOption[],
  currencies?: ISelectOption[]
) => {
  return [
    {
      label: 'Boost Group Name',
      name: 'name',
      required: true
    },

    {
      label: 'Country',
      name: 'country',
      required: true,
      options: countries
    },
    {
      label: 'Time Zone',
      name: 'timezone',
      required: true,
      options: timeZones
    },
    {
      label: 'Default Charger Screen Language',
      name: 'language',
      required: true,
      options: languages
    },
    {
      label: 'Currency',
      name: 'currency',
      required: true,
      options: currencies
    },
    {
      label: 'Boost LED Color',
      name: 'theme',
      required: true,
      options: boostLedColors
    },
    ...(ocpiCountryCode
      ? [
          {
            label: 'Country Code',
            name: 'ocpiCountryCode',
            disabled: true
          }
        ]
      : []),
    ...(ocpiPartyId
      ? [
          {
            label: 'Party ID',
            name: 'ocpiPartyId',
            disabled: true
          }
        ]
      : [])
  ];
};

export const defaultEdnOutputLimitPercentage = 90;
